import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaBell,
  FaSave,
  FaTimes,
  FaSpinner,
  FaImage,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const AdminCreateAnnouncement = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    priority: 0,
    link: "",
    linkText: "",
    targetAudience: "all",
    targetBranch: "",
    targetSemester: "",
    showAsPopup: false,
    expiresAt: "",
  });

  // Hide footer on this page
  React.useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  const [isDarkMode, setIsDarkMode] = useState(false);

  React.useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error("Title and message are required");
      return;
    }

    setLoading(true);

    const data = new FormData();
    data.append("title", formData.title);
    data.append("message", formData.message);
    data.append("type", formData.type);
    data.append("priority", formData.priority);
    data.append("targetAudience", formData.targetAudience);
    data.append("showAsPopup", formData.showAsPopup);

    if (formData.link) data.append("link", formData.link);
    if (formData.linkText) data.append("linkText", formData.linkText);
    if (formData.targetBranch)
      data.append("targetBranch", formData.targetBranch);
    if (formData.targetSemester)
      data.append("targetSemester", formData.targetSemester);
    if (formData.expiresAt) data.append("expiresAt", formData.expiresAt);
    if (formData.image) data.append("image", formData.image);

    try {
      const token = localStorage.getItem("token");
      await axios.post("/api/announcements/admin/create", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Announcement created successfully!");
      navigate("/admin");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create announcement",
      );
    } finally {
      setLoading(false);
    }
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const typeOptions = [
    { value: "info", label: "Info" },
    { value: "warning", label: "Warning" },
    { value: "success", label: "Success" },
    { value: "event", label: "Event" },
    { value: "promotion", label: "Promotion" },
    { value: "emergency", label: "Emergency" },
  ];

  const audienceOptions = [
    { value: "all", label: "All Students" },
    { value: "branch", label: "Specific Branch" },
    { value: "semester", label: "Specific Semester" },
  ];

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: "2rem",
      }}
    >
      <div style={{ maxWidth: "700px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          <button
            onClick={() => navigate("/admin")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "none",
              border: "none",
              color: "#8b5cf6",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <FaArrowLeft /> Back to Admin Dashboard
          </button>
        </div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: getCardBackground(),
            borderRadius: "24px",
            overflow: "hidden",
            border: `1px solid ${getBorderColor()}`,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "24px",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaBell size={24} color="white" />
              </div>
              <div>
                <h1
                  style={{ fontSize: "1.5rem", margin: 0, fontWeight: "bold" }}
                >
                  Create Announcement
                </h1>
                <p
                  style={{
                    fontSize: "0.85rem",
                    opacity: 0.9,
                    marginTop: "4px",
                  }}
                >
                  Send updates, alerts, or announcements to students
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: "28px" }}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
            >
              {/* Title */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: getTextColor(),
                  }}
                >
                  Title <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Clear and specific headline"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
              </div>

              {/* Message */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: getTextColor(),
                  }}
                >
                  Message <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  placeholder="Clear, concise message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  rows="3"
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontSize: "14px",
                    resize: "vertical",
                    outline: "none",
                  }}
                />
              </div>

              {/* Type and Priority */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    {typeOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Priority (0-10)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: parseInt(e.target.value) || 0,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                  <p
                    style={{
                      fontSize: "11px",
                      color: getTextSecondary(),
                      marginTop: "4px",
                    }}
                  >
                    Higher priority shows first (0 = lowest, 10 = highest)
                  </p>
                </div>
              </div>

              {/* Target Audience */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: getTextColor(),
                  }}
                >
                  Target Audience
                </label>
                <select
                  value={formData.targetAudience}
                  onChange={(e) =>
                    setFormData({ ...formData, targetAudience: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {audienceOptions.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Conditional Branch/Semester */}
              {formData.targetAudience === "branch" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Branch
                  </label>
                  <select
                    value={formData.targetBranch}
                    onChange={(e) =>
                      setFormData({ ...formData, targetBranch: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.targetAudience === "semester" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Semester
                  </label>
                  <select
                    value={formData.targetSemester}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        targetSemester: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="">Select Semester</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                      <option key={s} value={s}>
                        Semester {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Link and Link Text */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Link URL (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="https://..."
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Button Text
                  </label>
                  <input
                    type="text"
                    placeholder="View Details"
                    value={formData.linkText}
                    onChange={(e) =>
                      setFormData({ ...formData, linkText: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                </div>
              </div>

              {/* Expiry Date and Popup */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Expires At (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) =>
                      setFormData({ ...formData, expiresAt: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: getTextColor(),
                    }}
                  >
                    Show as Popup
                  </label>
                  <select
                    value={formData.showAsPopup}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        showAsPopup: e.target.value === "true",
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: getTextColor(),
                  }}
                >
                  Image (Optional)
                </label>
                <div
                  style={{
                    border: `2px dashed ${getBorderColor()}`,
                    borderRadius: "12px",
                    padding: "24px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => document.getElementById("imageInput").click()}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = "#8b5cf6")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor = getBorderColor())
                  }
                >
                  <FaImage size={32} color={getTextSecondary()} />
                  <p
                    style={{
                      fontSize: "13px",
                      color: getTextSecondary(),
                      marginTop: "8px",
                    }}
                  >
                    {imagePreview
                      ? "Click to change image"
                      : "Upload an image (JPG, PNG, GIF)"}
                  </p>
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "150px",
                        objectFit: "contain",
                        marginTop: "12px",
                        borderRadius: "8px",
                      }}
                    />
                  )}
                  <input
                    id="imageInput"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              {/* Created Date Info */}
              <div
                style={{
                  padding: "12px 16px",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.08)"
                    : "rgba(139,92,246,0.04)",
                  borderRadius: "12px",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: getTextSecondary(),
                    margin: 0,
                  }}
                >
                  <strong>Created:</strong> Will be set automatically when
                  published
                </p>
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={() => navigate("/admin")}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: loading
                      ? "#ccc"
                      : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {loading ? (
                    <FaSpinner
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  ) : (
                    <FaSave />
                  )}
                  {loading ? "Creating..." : "Publish Announcement"}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminCreateAnnouncement;
